/* ============================================================================
 * Buy More, Save More — Tier Views
 * ----------------------------------------------------------------------------
 * Single subscriber to `wc:tier-updated` from wc-tier-controller.js.
 * Paints every tier-aware surface from the cart state:
 *
 *   paintMounts(state):
 *     - [data-wc-tier-mount] elements (vertical/horizontal/cart-bar)
 *     - [data-wc-tier-subline] (PDP / sticky ATC / inside-ATC copy)
 *     - [data-wc-tier-sticky-text] (collection sticky bar dynamic copy)
 *
 *   applyState(state) — invoked on every event:
 *     - Toggles body.wc-has-tier-savings (for cart icon glow)
 *     - On tier-cross-up: maybeShowCelebration (cart drawer open) OR
 *       maybeShowPdpBanner (cart drawer closed)
 *     - Adds .is-just-crossed pulse to crossed tier element
 *     - Schedules a 220ms repaint to catch any cart.js DOM swap that
 *       finishes after this frame
 *
 *   Helpers:
 *     - spawnConfetti(parent, count) — particles fly outward inside overlay
 *     - initStickyBar()              — scroll-based show/hide for collection
 *     - watchCartDrawer()            — mutation observer triggers recompute
 * ========================================================================== */
(function () {
  'use strict';

  if (window.WCTierViews && window.WCTierViews.__inited) return;

  var EVENT_NAME = (window.WCTier && window.WCTier.EVENT_NAME) || 'wc:tier-updated';

  var prevTierIndex = -2;
  var prevCount = -1;
  // Last known BMSM savings (in cents) for the cart drawer counter
  // animation. Persists across cart.js DOM swaps via this closure.
  var prevBmsmCents = 0;

  // ─────────────────────────────────────────────────────────────────────
  // Counter animation — tick a number from `fromCents` to `toCents` over
  // `duration` ms with cubic-out easing. Used by both the cart drawer
  // savings line and the celebration overlay's "$X.XX" headline.
  // ─────────────────────────────────────────────────────────────────────
  function animateCount(el, fromCents, toCents, duration, formatter) {
    if (!el) return;
    fromCents = Math.round(fromCents);
    toCents = Math.round(toCents);
    formatter = formatter || function (c) { return '$' + (c / 100).toFixed(2); };
    if (fromCents === toCents) {
      el.textContent = formatter(toCents);
      return;
    }
    if (typeof requestAnimationFrame !== 'function') {
      el.textContent = formatter(toCents);
      return;
    }
    var start = (typeof performance !== 'undefined' && performance.now)
      ? performance.now() : Date.now();
    var diff = toCents - fromCents;
    function tick(now) {
      var t = Math.min(1, (now - start) / duration);
      var ease = 1 - Math.pow(1 - t, 3); // cubic-out
      var current = Math.round(fromCents + diff * ease);
      el.textContent = formatter(current);
      if (t < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  // ─────────────────────────────────────────────────────────────────────
  // Paint pipeline
  // ─────────────────────────────────────────────────────────────────────
  function paintMounts(state) {
    document.querySelectorAll('[data-wc-tier-mount]').forEach(function (mount) {
      updateMount(mount, state);
    });
    document.querySelectorAll('[data-wc-tier-subline]').forEach(function (el) {
      paintSubline(el, state);
    });
    document.querySelectorAll('[data-wc-tier-sticky-text]').forEach(function (el) {
      paintStickyMsg(el, state);
    });
  }

  // ─────────────────────────────────────────────────────────────────────
  // Header cart icon bubble — count badge + tier % OFF pill.
  // Server-rendered Liquid normally keeps these in sync via cart.js
  // section refreshes when items are added/removed. But on bfcache
  // restore (swipe-back from a PDP where the cart was modified), the
  // cached HTML is stale. We paint the values from controller state
  // directly so a fresh recompute → applyState chain reconciles the
  // bubble with /cart.js reality, no section fetch needed.
  // ─────────────────────────────────────────────────────────────────────
  function paintHeaderBubble(state) {
    var anchor = document.getElementById('cart-icon-bubble');
    if (!anchor) return;

    // Count badge: the inner span carries the displayed number; an
    // ariel sibling holds the screen-reader count. Update both.
    var bubble = anchor.querySelector('.cart-count-bubble');
    if (bubble) {
      var visible = bubble.querySelector('span[aria-hidden="true"]');
      if (visible) {
        visible.textContent = state.count >= 100 ? '99+' : String(state.count);
      }
      var sr = bubble.querySelector('.visually-hidden');
      if (sr) {
        sr.textContent = state.count + ' item' + (state.count === 1 ? '' : 's') + ' in cart';
      }
    }

    // Tier % OFF pill: append/remove based on state. Matches the Liquid
    // markup so CSS targeting stays the same.
    var tierPill = anchor.querySelector('.wc-cart-tier-bubble');
    if (state.off > 0) {
      if (!tierPill) {
        tierPill = document.createElement('div');
        tierPill.className = 'wc-cart-tier-bubble';
        tierPill.setAttribute('aria-hidden', 'true');
        anchor.appendChild(tierPill);
      }
      tierPill.textContent = state.off + '% OFF';
    } else if (tierPill) {
      tierPill.parentNode.removeChild(tierPill);
    }
  }

  function applyState(state) {
    // Top-tier achieved (6+ items) → disables the 50% OFF pulse on
    // ladders/labels via CSS. Once the customer has unlocked the top
    // tier, the celebratory pulse stops "selling" the deal — they've
    // already got it. CSS rules in wc-tier-ladder.css use this class.
    document.body.classList.toggle('wc-at-top-tier', !!state.atTop);

    // Tier crossed UP — fire celebration overlay in the cart drawer.
    // (PDP slide-in banner has moved off this hook; it now fires on
    // PDP page load instead — see initPdpBanner.)
    var crossedUp = prevCount >= 0 && state.count > prevCount && state.count >= 1;
    if (crossedUp) {
      maybeShowCelebration(prevCount, state.count, state);
    }

    // ATC "✓ Added!" flash is now wired to the CLICK event directly
    // (see bindAtcClickFlash in init) so it fires before the cart
    // drawer auto-opens and obscures the button. Wiring it to
    // tier-updated meant the flash fired ~300ms too late.

    paintMounts(state);
    paintBmsmSavingsLine(state);
    paintHeaderBubble(state);

    if (prevTierIndex >= 0 && state.tierIndex > prevTierIndex) {
      var qty = state.tierIndex + 1;
      document.querySelectorAll(
        '[data-wc-tier-mount] [data-tier-qty="' + qty + '"]'
      ).forEach(function (el) {
        el.classList.add('is-just-crossed');
        setTimeout(function () { el.classList.remove('is-just-crossed'); }, 800);
      });
    }

    var stateChanged =
      (prevCount !== state.count) || (prevTierIndex !== state.tierIndex);
    prevTierIndex = state.tierIndex;
    prevCount = state.count;

    // Re-paint after a tick to catch any cart.js DOM swap that finishes
    // AFTER this paint (fixes the empty-bar-after-add bug). Skip it
    // when neither count nor tierIndex changed — there's nothing for
    // the late paint to reconcile in that case, so we save a layout
    // pass on every visibilitychange / pageshow recompute.
    if (stateChanged) {
      setTimeout(function () { paintMounts(state); }, 220);
    }
  }

  function updateMount(mount, state) {
    var variant = mount.getAttribute('data-variant');
    if (variant === 'cart-bar') updateCartBar(mount, state);
    else                        updateTierItems(mount, state);
  }

  function updateTierItems(mount, state) {
    mount.querySelectorAll('[data-tier-qty]').forEach(function (el) {
      var qty = parseInt(el.getAttribute('data-tier-qty'), 10);
      var unlocked = state.count >= qty;
      var current  = (state.tierIndex + 1) === qty;
      el.classList.toggle('is-unlocked', unlocked);
      el.classList.toggle('is-current', current);
    });
  }

  function updateCartBar(mount, state) {
    updateTierItems(mount, state);

    var pct;
    if (state.count <= 0)      pct = 0;
    else if (state.count >= 6) pct = 100;
    else                       pct = Math.round((state.count / 6) * 100);

    var fill = mount.querySelector('[data-wc-tier-progress]');
    if (fill) {
      fill.style.width = pct + '%';
      fill.classList.toggle('is-full', pct >= 100);
      fill.setAttribute('data-tier-level', String(state.tierIndex < 0 ? 0 : state.tierIndex));
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // Subline copy (PDP + sticky ATC)
  // ─────────────────────────────────────────────────────────────────────
  function paintSubline(root, state) {
    var icon = root.querySelector('.wc-tier-subline__icon');
    var text = root.querySelector('.wc-tier-subline__text');
    if (!icon || !text) return;

    var html;
    if (state.count === 0) {
      icon.textContent = '🔥';
      html = 'Mix any <strong>2+ shirts</strong> &rarr; unlock <strong>10% OFF</strong>';
    } else if (state.count === 1) {
      // which felt like a downer. New copy mirrors the empty-cart format.
      icon.textContent = '🔥';
      html = 'Add <strong>1+ shirt</strong> &rarr; unlock <strong>10% OFF</strong>';
    } else if (state.atTop) {
      icon.textContent = '🎉';
      html = '<strong>50% OFF</strong> sitewide';
    } else {
      icon.textContent = '✓';
      html = '<strong>' + state.off + '% OFF</strong> unlocked &mdash; add 1 more for <strong>' + state.nextOff + '% OFF</strong>';
    }
    text.innerHTML = html;
  }

  // ─────────────────────────────────────────────────────────────────────
  // Collection sticky-strip copy — drops "get", uses "X% OFF" format
  // ─────────────────────────────────────────────────────────────────────
  function paintStickyMsg(el, state) {
    var n = state.count;
    var html;
    // 0-1 items: pre-cart copy uses "mix" (encourages bundle thinking).
    // 2+: customer is mid-bundle, switch to "add" — they're not browsing
    // a fresh mix anymore, they're climbing the ladder one step at a time.
    if (n === 0)      html = 'mix any 3 shirts = <em>20% OFF</em>';
    else if (n === 1) html = 'mix 2 more shirts = <em>20% OFF</em>';
    else if (n === 2) html = 'add 1 more shirt = <em>20% OFF</em>';
    else if (n === 3) html = 'add 1 more shirt = <em>30% OFF</em>';
    else if (n === 4) html = 'add 1 more shirt = <em>40% OFF</em>';
    else if (n === 5) html = 'add 1 more shirt = <em>50% OFF</em>';
    else              html = "you've unlocked <em>50% OFF</em> sitewide";
    el.innerHTML = html;
    // Hide the empty checkbox circle once at the top tier (the "unlocked"
    // copy doesn't need a "next-step" marker). Walk up to the parent bar.
    var bar = el.closest ? el.closest('.wc-tier-sticky-bar') : null;
    if (bar) bar.classList.toggle('is-unlocked', state.atTop);
  }

  // ─────────────────────────────────────────────────────────────────────
  // Celebration overlay — text REPLACES; 350ms delay before mount so the
  // cart.js DOM swap completes first.
  // ─────────────────────────────────────────────────────────────────────
  var activeTimers = [];
  function clearTimers() {
    activeTimers.forEach(clearTimeout);
    activeTimers = [];
  }

  // Celebration savings = state.paidSubtotalCents × tier_off / 100.
  // Pulled live from the controller (which mirrors the Liquid math in
  // snippets/cart-drawer.liquid), so the overlay figure matches the
  // cart-drawer "Buy More, Save More savings" line for any mix of items.
  function nextOffAt(c) {
    if (c >= 6) return 50;
    if (c < 1) return 10;
    return [10, 10, 20, 30, 40, 50][c]; // c=1→10, 2→20 ... 5→50
  }

  function maybeShowCelebration(prev, now, state) {
    if (now < 1) return;

    // Tier 1 (free-gift unlock): only fire if the first item actually
    // crossed the $25 threshold that unlocks the gift. Stickers,
    // bumper stickers, and magnets all fall under $25 individually —
    // for those, the gift isn't unlocked and the celebration would
    // be misleading. Matches the cart-drawer Liquid threshold check
    // (`cart.total_price >= 2500`).
    if (now === 1 && (!state || (state.cartTotal || 0) < 2500)) return;

    clearTimers();

    var paidSub = (state && typeof state.paidSubtotalCents === 'number') ? state.paidSubtotalCents : 0;
    var tierOff = (state && typeof state.off === 'number') ? state.off : 0;
    var savedCents = Math.round((paidSub * tierOff) / 100);
    var savedDollars = (savedCents / 100).toFixed(2);

    // Tier 1 (first item added): celebrate the free-gift unlock — no
    // savings counter yet (no tier discount until 2 items). Tiers 2+:
    // animated $X.XX savings counter. Visible counter is aria-hidden;
    // a sibling visually-hidden span carries the final-value
    // announcement once via aria-live="polite".
    var primaryHTML;
    if (now === 1) {
      primaryHTML =
        'Congrats! You<br>got a <em>free gift</em>!' +
        '<span class="visually-hidden" data-wc-celebration-sr></span>';
    } else {
      primaryHTML =
        'You just saved<br>' +
        '<strong>$<span data-wc-celebration-counter aria-hidden="true">0.00</span>!</strong>' +
        '<span class="visually-hidden" data-wc-celebration-sr></span>';
    }

    // Two-stage flow for tiers 2-6: primary "$X saved" → secondary.
    // 7+ adds are single-stage (just the climbing counter, no secondary
    // — the tier-6 message would be repetitive after that point).
    // midHTML is unused; kept as an empty placeholder so the existing
    // mountOverlay assignment remains a no-op.
    var midHTML = '';
    var secondaryHTML = '';
    if (now === 6) {
      secondaryHTML = "You've unlocked<br><em>50% OFF</em> sitewide";
    } else if (now < 6) {
      secondaryHTML = 'Add 1 more shirt<br>to unlock <em>' + nextOffAt(now) + '% OFF</em>';
    }

    // .wc-cart-b3g3f (which gets innerHTML-wiped by cart.js on every
    // cart update). Retry up to 10× over 1s if the drawer isn't found
    // on first attempt — covers the case where applyState runs before
    // the initial drawer renders.
    var attempt = 0;
    function tryMount() {
      var drawer = document.querySelector('cart-drawer');
      if (!drawer) {
        if (attempt++ < 10) {
          activeTimers.push(setTimeout(tryMount, 100));
        }
        return;
      }
      mountOverlay(drawer);
    }

    function mountOverlay(drawer) {
      if (getComputedStyle(drawer).position === 'static') {
        drawer.style.position = 'relative';
      }

      var overlay = drawer.querySelector('.wc-tier-celebration');
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.className = 'wc-tier-celebration';
        overlay.setAttribute('aria-live', 'polite');
        overlay.innerHTML =
          '<div class="wc-tier-celebration__msg wc-tier-celebration__msg--primary"></div>' +
          '<div class="wc-tier-celebration__msg wc-tier-celebration__msg--mid"></div>' +
          '<div class="wc-tier-celebration__msg wc-tier-celebration__msg--secondary"></div>';
        drawer.appendChild(overlay);
      }
      overlay.querySelector('.wc-tier-celebration__msg--primary').innerHTML = primaryHTML;
      overlay.querySelector('.wc-tier-celebration__msg--mid').innerHTML = midHTML;
      overlay.querySelector('.wc-tier-celebration__msg--secondary').innerHTML = secondaryHTML;
      // Reset to initial state then force a reflow so the browser commits
      // the "before" state before we add .is-visible — guarantees the
      // opacity/transform transition fires (without this, a freshly-
      // created element + immediate class-add can be painted in final
      // state with no animation).
      overlay.classList.remove('is-stage-mid', 'is-stage2', 'is-visible');
      // eslint-disable-next-line no-unused-expressions
      void overlay.offsetWidth;
      overlay.classList.add('is-visible');

      // Counter animation on the primary "$X.XX" amount. Starts after
      // the fade-in window so the user sees the count climb into view.
      // The span is bare-amount only (no leading "$") — formatter
      // returns "X.XX" without prefix. SR-only sibling announces the
      // final value once after the count completes (~1000ms).
      activeTimers.push(setTimeout(function () {
        var counter = overlay.querySelector('[data-wc-celebration-counter]');
        if (counter) {
          animateCount(counter, 0, savedCents, 800, function (c) {
            return (c / 100).toFixed(2);
          });
        }
      }, 200));
      activeTimers.push(setTimeout(function () {
        var sr = overlay.querySelector('[data-wc-celebration-sr]');
        if (!sr) return;
        sr.textContent = (now === 1)
          ? 'Congrats, you got a free gift.'
          : 'You just saved $' + savedDollars + '.';
      }, 1100));

      // Confetti burst on tier crossing — a dozen small particles fly
      // outward from the overlay center. Pure CSS animation; JS only
      // spawns the elements with random vectors and removes them after.
      spawnConfetti(overlay, 14);

      // Counter finishes at t=1000 (200ms wait + 800ms tick).
      // Hold the final amount for 550ms, then cross-fade.
      if (now > 6) {
        // 7+ adds: single-stage. Hold the climbing $X saved primary,
        // then fade out — no secondary message.
        activeTimers.push(setTimeout(function () {
          overlay.classList.remove('is-visible');
        }, 1550));
      } else {
        // Tiers 2-6: two-stage flow. Tier 6 used to have an extra
        // "Wow! What an amazing deal!" mid stage between primary and
        // t=1550 → secondary (Add 1 more shirt … / You've unlocked …)
        // t=2750 → fade out (secondary visible for 1200ms)
        activeTimers.push(setTimeout(function () {
          overlay.classList.add('is-stage2');
        }, 1550));
        activeTimers.push(setTimeout(function () {
          overlay.classList.remove('is-visible');
        }, 2750));
      }
    }

    tryMount();
  }

  // ─────────────────────────────────────────────────────────────────────
  // Cart drawer Buy-More-Save-More savings line — animate the dollar
  // amount from previous value (closure) to current rendered value
  // whenever cart state changes. Triggered from applyState. The element
  // is rendered server-side via cart-drawer.liquid with a data-wc-cents
  // attribute (raw cents value) and data-wc-bmsm-savings as the hook.
  // ─────────────────────────────────────────────────────────────────────
  function paintBmsmSavingsLine(state) {
    var el = document.querySelector('[data-wc-bmsm-savings]');
    if (!el) {
      // Row isn't rendered (cart < 2 items or no qualifying products).
      // Reset closure so the next time it appears we count from 0.
      prevBmsmCents = 0;
      return;
    }
    // Read the truth from the just-rendered Liquid (data attribute is
    // always accurate since cart.js fetched fresh HTML before this fires).
    var newCents = parseInt(el.getAttribute('data-wc-cents'), 10) || 0;
    if (newCents === prevBmsmCents) return;
    animateCount(el, prevBmsmCents, newCents, 600);
    prevBmsmCents = newCents;
  }

  // ─────────────────────────────────────────────────────────────────────
  // ATC button "✓ Added!" flash — 700ms green state immediately on
  // click. Fires BEFORE the cart drawer auto-opens (which would obscure
  // the button). Capture-phase listener so it runs before product-form
  // submit handlers steal focus / start spinners.
  // ─────────────────────────────────────────────────────────────────────
  var ATC_FLASH_MS = 700;
  function flashAtcAddedOn(btn) {
    if (!btn) return;
    if (btn.dataset.wcFlashing === '1') return; // debounce double-click
    var label = btn.querySelector('.wc-atc-btn__text');
    if (!label) return;

    btn.dataset.wcFlashing = '1';
    btn.classList.add('wc-atc-btn--just-added');
    var original = label.textContent;
    label.textContent = '✓ Added!';

    setTimeout(function () {
      btn.classList.remove('wc-atc-btn--just-added');
      // Don't clobber if global.js's toggleAddButton (variant change)
      // wrote a fresh value during the window.
      if (label.textContent === '✓ Added!') {
        label.textContent = original;
      }
      btn.dataset.wcFlashing = '0';
    }, ATC_FLASH_MS);
  }

  function bindAtcClickFlash() {
    document.addEventListener('click', function (e) {
      // .closest matches the click whether the user tapped the button
      // itself or any child element (label, spinner div).
      var btn = e.target.closest && e.target.closest('.wc-atc-btn');
      if (!btn) return;
      // Skip disabled / sold-out / mid-add states.
      if (btn.disabled) return;
      if (btn.getAttribute('aria-disabled') === 'true') return;
      if (btn.classList.contains('loading')) return;
      flashAtcAddedOn(btn);
    }, true); // capture so we run before product-form's submit handler
  }

  // ─────────────────────────────────────────────────────────────────────
  // Confetti — spawned into a parent element, particles fly outward
  // and self-remove after the CSS animation finishes.
  // ─────────────────────────────────────────────────────────────────────
  var CONFETTI_COLORS = ['#FFD700', '#cc0000', '#fff', '#2d8a4e'];
  function spawnConfetti(parent, count) {
    if (!parent) return;
    var existing = parent.querySelectorAll('.wc-confetti');
    existing.forEach(function (el) { el.remove(); });
    for (var i = 0; i < count; i++) {
      var dot = document.createElement('span');
      dot.className = 'wc-confetti';
      var angle = (Math.PI * 2) * (i / count) + (Math.random() - 0.5) * 0.6;
      var distance = 90 + Math.random() * 90;
      var dx = Math.cos(angle) * distance;
      var dy = Math.sin(angle) * distance - 20; // slight upward bias
      var rot = (Math.random() * 720 - 360) + 'deg';
      var color = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
      dot.style.setProperty('--dx', dx.toFixed(0) + 'px');
      dot.style.setProperty('--dy', dy.toFixed(0) + 'px');
      dot.style.setProperty('--rot', rot);
      dot.style.setProperty('--bg', color);
      dot.style.animationDelay = (i * 12) + 'ms';
      parent.appendChild(dot);
    }
    // Clean up after animation completes (1400ms + last delay).
    setTimeout(function () {
      parent.querySelectorAll('.wc-confetti').forEach(function (el) { el.remove(); });
    }, 1700);
  }

  // ─────────────────────────────────────────────────────────────────────
  // PDP page-load banner — fires on every PDP load with current tier
  // status copy. Slide-in toast from the right; auto-hides after ~3.5s.
  // ─────────────────────────────────────────────────────────────────────
  var pdpBannerTimer = null;
  var PDP_BANNER_SUPPRESS_KEY = 'wc-pdp-banner-last-shown';
  var PDP_BANNER_SUPPRESS_MS  = 3 * 60 * 1000;
  function initPdpBanner() {
    // Only fire on product pages. Detect via:
    //  1. <product-info> custom element (Dawn standard, PDP-only), or
    //  2. body.product-page class (the theme.liquid layout adds
    //     `{{ template.name }}-page` to body — "product" template
    //     becomes "product-page").
    var isPdp = document.querySelector('product-info')
             || document.body.classList.contains('product-page');
    if (!isPdp) return;

    // Mobile-only — desktop has more screen real estate and the BMSM
    // strip on the collection page already communicates the offer; the
    // pop-in adds noise without earning attention.
    if (window.innerWidth >= 750) return;

    // Throttle: after a successful show, suppress for 3 minutes so a
    // user who taps through several PDPs in succession (or accidentally
    // taps a product and swipes back) doesn't see the same nudge again
    // and again. Persisted in localStorage so it survives navigations.
    try {
      var last = parseInt(localStorage.getItem(PDP_BANNER_SUPPRESS_KEY) || '0', 10);
      if (last && (Date.now() - last) < PDP_BANNER_SUPPRESS_MS) return;
    } catch (e) { /* localStorage may be unavailable; fall through */ }

    function fire() {
      var s = window.WCTier && window.WCTier.getState && window.WCTier.getState();
      if (!s) {
        // Controller's first /cart.js fetch hasn't resolved yet — try
        // again in 250ms. Cap retries to avoid spinning forever.
        if (initPdpBanner._tries === undefined) initPdpBanner._tries = 0;
        if (initPdpBanner._tries++ < 12) setTimeout(fire, 250);
        return;
      }
      showPdpStatusBanner(s);
      try { localStorage.setItem(PDP_BANNER_SUPPRESS_KEY, String(Date.now())); }
      catch (e) { /* ignore */ }
    }
    fire();
  }

  function showPdpStatusBanner(state) {
    var html;
    if (state.atTop) {
      // 6+ items — top tier achieved.
      html = '<span class="wc-pdp-tier-banner__icon">🎉</span>' +
             '<span class="wc-pdp-tier-banner__text">You\'ve unlocked <strong>50% OFF</strong></span>';
    } else if (state.count === 0) {
      // Empty cart — encourage starting the bundle.
      html = '<span class="wc-pdp-tier-banner__icon">🔥</span>' +
             '<span class="wc-pdp-tier-banner__text">Mix <strong>2+ shirts</strong> &rarr; unlock <strong>10% OFF</strong></span>';
    } else {
      // 1-5 items — "Add 1+ shirts → unlock NEXT_TIER% off". state.nextOff
      // gives the next tier %off (1→10, 2→20, 3→30, 4→40, 5→50).
      html = '<span class="wc-pdp-tier-banner__icon">🔥</span>' +
             '<span class="wc-pdp-tier-banner__text">Add <strong>1+ shirts</strong> &rarr; unlock <strong>' + state.nextOff + '% OFF</strong></span>';
    }

    var banner = document.querySelector('.wc-pdp-tier-banner');
    if (!banner) {
      banner = document.createElement('div');
      banner.className = 'wc-pdp-tier-banner';
      banner.setAttribute('aria-live', 'polite');
      document.body.appendChild(banner);
    }
    banner.innerHTML = html;
    void banner.offsetWidth; // force reflow so transition fires
    banner.classList.add('is-visible');

    // Empty cart → 1.5s (give first-time visitors time to read the
    // "unlock 10% off" prompt). 1+ items → 1s (returning shopper
    // already knows the offer; a quicker glance is enough).
    var dwellMs = state.count >= 1 ? 1000 : 1500;
    if (pdpBannerTimer) clearTimeout(pdpBannerTimer);
    pdpBannerTimer = setTimeout(function () {
      banner.classList.remove('is-visible');
    }, dwellMs);
  }

  // ─────────────────────────────────────────────────────────────────────
  // Collection sticky bar visibility — scroll-based, header-height-aware
  // ─────────────────────────────────────────────────────────────────────
  function initStickyBar() {
    var ladder = document.querySelector('[data-wc-tier-ladder-anchor]');
    var bar    = document.querySelector('[data-wc-tier-sticky-bar]');
    if (!ladder || !bar) return;

    function check() {
      var headerH = parseInt(
        getComputedStyle(document.documentElement)
          .getPropertyValue('--wc-header-height') || '64',
        10
      ) || 64;

      // Desktop: trigger when the product grid's TOP edge slides under
      // the fixed header — the ladder is short up there and there's a
      // reviews strip + sort/filter between it and the grid; firing on
      // ladder.bottom would show the bar way before the grid is in view.
      // Mobile: trigger on ladder.bottom (the vertical ladder is tall
      // and basically the last thing before the grid).
      var trigger = false;
      if (window.innerWidth >= 750) {
        var grid = document.querySelector('#product-grid');
        if (grid) {
          trigger = grid.getBoundingClientRect().top <= headerH;
        }
      } else {
        trigger = ladder.getBoundingClientRect().bottom <= headerH;
      }
      bar.classList.toggle('is-visible', trigger);
    }
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    check();
  }

  function setHeaderHeight() {
    // Use the sticky-header element when present — it represents the
    // portion that actually pins to the top during scroll. Falling back
    // to the section-header / header-group included the announcement
    // bar above, which scrolls away once sticky activates → producing a
    // visible gap between the menu's bottom edge and the wc-tier-
    // sticky-bar (which positions itself by --wc-header-height).
    var sticky = document.querySelector('sticky-header');
    var stickyInner = sticky ? sticky.querySelector('.header-wrapper') || sticky : null;
    var el = stickyInner
          || document.querySelector('.shopify-section-group-header-group')
          || document.querySelector('.section-header');
    if (!el) return;
    // Buffer was sized for the old measurement target (whole header
     // group); now that we're measuring only the sticky portion, no
     // buffer is needed on mobile. Desktop keeps a small buffer to
     // clear the wider header's drop-shadow.
    var buffer = window.innerWidth >= 990 ? -4 : -3;
    var h = Math.round(el.getBoundingClientRect().height) + buffer;
    document.documentElement.style.setProperty('--wc-header-height', h + 'px');
  }

  function watchCartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    var obs = new MutationObserver(function () {
      if (drawer.classList.contains('active') || drawer.classList.contains('is-open')) {
        if (window.WCTier && typeof window.WCTier.recompute === 'function') {
          window.WCTier.recompute();
        } else if (window.WCTier && typeof window.WCTier.getState === 'function') {
          var s = window.WCTier.getState();
          if (s) paintMounts(s);
        }
      }
    });
    obs.observe(drawer, { attributes: true, attributeFilter: ['class'] });

    // Belt-and-suspenders: cart icon clicks force a recompute too.
    document.querySelectorAll('#cart-icon-bubble, [data-cart-open], a[href="/cart"]').forEach(function (b) {
      b.addEventListener('click', function () {
        setTimeout(function () {
          if (window.WCTier && typeof window.WCTier.recompute === 'function') {
            window.WCTier.recompute();
          }
        }, 50);
      });
    });
  }

  function init() {
    document.addEventListener(EVENT_NAME, function (e) { applyState(e.detail); });
    initStickyBar();
    watchCartDrawer();
    setHeaderHeight();
    // Resize fires infrequently — call directly. Scroll fires every
    // frame on mobile and the header height almost never actually
    // changes mid-scroll, so coalesce to one rAF tick to avoid
    // thrashing layout. requestAnimationFrame deduplicates: if a
    // tick is already pending we skip scheduling another.
    var headerHeightRaf = 0;
    function scheduleHeaderHeight() {
      if (headerHeightRaf) return;
      headerHeightRaf = requestAnimationFrame(function () {
        headerHeightRaf = 0;
        setHeaderHeight();
      });
    }
    window.addEventListener('resize', setHeaderHeight);
    window.addEventListener('scroll', scheduleHeaderHeight, { passive: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(setHeaderHeight);

    if (window.WCTier && typeof window.WCTier.getState === 'function') {
      var s = window.WCTier.getState();
      if (s) {
        prevCount = s.count;
        prevTierIndex = s.tierIndex;
        paintMounts(s);
      }
    }

    initPdpBanner();
    bindAtcClickFlash();

    window.WCTierViews = { __inited: true, paintMounts: paintMounts };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();